import styled from 'styled-components';
import Label from '@/app/components/search/components/Filters/HorizontalFilters/Pill/Label.tsx';
import FilterContainer from '@/app/components/search/components/Filters/verticalFilters/FilterContainer.ts';
import type { Filter, FilterDataMultiSelect, FiltersGroup } from '@/app/common/filters/core/filtersTypes.ts';
import { checkIsConsecutive } from '@/app/components/search/components/Filters/HorizontalFilters/Pill/utils.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { LabelText } from '@/app/components/search/components/Filters/HorizontalFilters/Pill/styled.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { isGermany } from '@/app/common/components/Stars/utils.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import Stars from '@/app/common/components/Stars/index.tsx';
import { starsSelectionOf, starsSelectionText } from '@/app/common/filters/core/starsLabel.ts';

const getStarsValuesForLabel = (values: number[]) => {
  if (values.length > 1) {
    const sortedValues: number[] = values.sort((a, b) => a - b).map((x) => x);
    const isConsecutive = checkIsConsecutive(sortedValues);

    if (isConsecutive) return `${sortedValues[0]} - ${sortedValues[values.length - 1]}`;

    return sortedValues.join(' - ');
  }

  return `${values[0]}`;
};

const getStarsLabel = ({ t, values }: { t: TranslationFunction; values: number[] }) => {
  // in DE market we can't mention stars
  // for this reason this key was not translated in DE
  // so we are using a fallback for this case
  const defaultTranslation = t('vacation.oss.filters.stars.placeholder');
  const deTranslation = '__number__';
  const translation = defaultTranslation || deTranslation;

  return translation.replace('__number__', getStarsValuesForLabel(values));
};

const EvoStarsLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const StarsSelectionLabel = ({ filters }: FiltersGroup) => {
  const { t } = useTranslation();
  const { useEvoFilters } = useFiltersUiData();
  // Same source Stars reads, so the sun's presence and its glyph cannot disagree.
  const { localization } = usePageWrapperData();

  const data = filters.find((f: Filter) => f.name === FILTER_NAME.STARS)?.data;
  const values = (data as FilterDataMultiSelect).options
    .filter((item) => item.selected)
    .map((item) => parseInt(item.value, 10));

  if (useEvoFilters) {
    const { text, bareNumbers, suffix } = starsSelectionText(starsSelectionOf(filters), t);
    // DE has no wrapper translation, so a sun qualifies the bare numbers instead.
    const showGlyph = isGermany(localization) && bareNumbers;

    return (
      <EvoStarsLabel>
        <LabelText variant="BodySmall" as="span">
          {text}
        </LabelText>
        {showGlyph && <Stars stars={1} color="currentColor" decorative={true} />}
        {suffix && (
          <LabelText variant="BodySmall" as="span">
            {suffix.trim()}
          </LabelText>
        )}
      </EvoStarsLabel>
    );
  }

  return <Label label={getStarsLabel({ t, values })} control={FilterContainer.StarsContainer} selected={true} />;
};
